import { useState, useEffect } from "react";
import logger from "../utils/logger";

const defaultSettings = {
  enableHighAccuracy: true, // Enable high accuracy for better initial position
  timeout: 10000, // Increased timeout for iOS Safari (can be slower)
  maximumAge: 60000, // Allow cached positions up to 60 seconds old
};

export const usePosition = (watch = false, lazy = false, userSettings = {}) => {
  // Helper function to detect iOS Safari
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    return iOS && webkit && !/(CriOS|FxiOS|OPiOS|mercury)/i.test(ua);
  };

  // Adjust settings for iOS Safari if needed
  const getOptimizedSettings = () => {
    const baseSettings = { ...defaultSettings, ...userSettings };

    if (isIOSSafari()) {
      return {
        ...baseSettings,
        // iOS Safari works better with these settings
        timeout: Math.max(baseSettings.timeout, 15000), // Ensure minimum 15s timeout
        enableHighAccuracy: false, // Sometimes high accuracy causes issues on iOS
      };
    }

    return baseSettings;
  };

  const settings = getOptimizedSettings();

  // Helper function to detect if permissions API is available and reliable
  const canUsePermissionsAPI = () => {
    return (
      !isIOSSafari() &&
      navigator.permissions &&
      navigator.permissions.query &&
      typeof navigator.permissions.query === "function"
    );
  };

  const [position, setPosition] = useState({});
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isInitialized, setIsInitialized] = useState(!lazy);
  const [isLoading, setIsLoading] = useState(false);

  const onChange = ({ coords, timestamp }) => {
    setError(null);
    setHasPermission(true);
    setIsLoading(false);
    setPosition({
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: coords.accuracy,
      speed: coords.speed,
      heading: coords.heading,
      timestamp,
    });
  };

  const onError = (error) => {
    logger.log("LOCATION ERROR", error);
    setError(error);
    setPosition({});
    setIsLoading(false);

    // Handle different error codes appropriately
    switch (error.code) {
      case 1: // PERMISSION_DENIED
        setHasPermission(false);
        break;
      case 2: // POSITION_UNAVAILABLE
        // Don't change permission state - it might still be granted
        break;
      case 3: // TIMEOUT
        // Don't change permission state - it might still be granted
        break;
      default:
        setHasPermission(false);
        break;
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    if (!navigator || !navigator.geolocation) {
      setError("Geolocation is not supported");
      setHasPermission(false);
      return;
    }

    setIsLoading(true);

    if (canUsePermissionsAPI()) {
      // Use permissions API for browsers that support it reliably (not iOS Safari)
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          setHasPermission(result.state === "granted");

          // Listen for permission changes
          result.addEventListener("change", () => {
            setHasPermission(result.state === "granted");
            if (result.state === "granted") {
              navigator.geolocation.getCurrentPosition(
                onChange,
                onError,
                settings
              );
            }
          });

          // If permission is granted, get position immediately
          if (result.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              onChange,
              onError,
              settings
            );
          } else if (result.state === "prompt") {
            // Permission needs to be requested
            navigator.geolocation.getCurrentPosition(
              onChange,
              onError,
              settings
            );
          }
        })
        .catch((permissionError) => {
          // Fallback if permissions API fails
          logger.log(
            "Permissions API failed, falling back to direct geolocation",
            permissionError
          );
          navigator.geolocation.getCurrentPosition(onChange, onError, settings);
        });
    } else {
      // For iOS Safari and other browsers without reliable permissions API
      // Try getting position directly and handle permission through error codes
      navigator.geolocation.getCurrentPosition(
        onChange,
        (error) => {
          if (error.code === 1) {
            // PERMISSION_DENIED
            setHasPermission(false);
          }
          onError(error);
        },
        settings
      );
    }

    // Then set up watching if requested
    if (watch) {
      const watcher = navigator.geolocation.watchPosition(
        onChange,
        onError,
        settings
      );
      return () => navigator.geolocation.clearWatch(watcher);
    }
  }, [
    settings.enableHighAccuracy,
    settings.timeout,
    settings.maximumAge,
    watch,
    isInitialized,
  ]);

  const initializePosition = () => {
    setIsInitialized(true);
  };

  return {
    ...position,
    positionError: error,
    hasPermission,
    isLoading,
    initializePosition,
  };
};
